"use client";

import React, { useState, useEffect } from "react";
import DesktopHeader from "@/src/components/layout/header/DesktopHeader";
import MobileHeader from "@/src/components/layout/header/MobileHeader";
import { usePathname } from "next/navigation";
import BackButton from "@/src/shared/components/atoms/BackButton";
import { noBackButtonPathsForMobileDevice } from "@/src/shared/constants/app";
import { type IFilter } from "@/src/shared/features/search";

interface IHeaderProps {
  headerRef: React.RefObject<HTMLDivElement | null>;
  isSearchActive: boolean;
  setIsSearchActive: React.Dispatch<React.SetStateAction<boolean>>;
  activeSearchSection: "where" | "dates" | "guests" | null;
  setActiveSearchSection: React.Dispatch<
    React.SetStateAction<"where" | "dates" | "guests" | null>
  >;
  headerState: "top" | "scrolled";
  isMobile: boolean;
  bottomNavVisible: boolean; // Added this prop
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  activateSearch: (section: "where" | "dates" | "guests") => void;
  closeSearch: () => void;
  clearDestination: () => void;
  clearDates: () => void;
  clearGuests: () => void;
  updateGuestCount: (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => void;
  getTotalGuests: () => string;
  handleSearch: (next?: IFilter) => void;
}

const Header: React.FC<IHeaderProps> = (props) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const pathname = usePathname() ?? "";

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Don't render dynamic content until hydrated to prevent SSR mismatch
  if (!isHydrated) {
    return (
      <>
        <div className="hidden md:flex h-16 items-center justify-between gap-4 px-8 min-[1440px]:px-12">
          <div className="w-[130px] h-8 bg-gray-200 animate-pulse rounded" />
          <div className="flex-1 flex justify-center">
            <div className="w-full max-w-[480px] min-w-[320px] h-[52px] bg-gray-200 animate-pulse rounded-full" />
          </div>
          <div className="flex items-center gap-8">
            <div className="w-[102px] h-4 bg-gray-200 animate-pulse rounded" />
            <div className="w-[74px] h-[42px] bg-gray-200 animate-pulse rounded-full" />
          </div>
        </div>
        <div className="md:hidden px-4 pt-3 pb-1">
          {pathname.startsWith("/s/") ? (
            <div className="flex items-center gap-3">
              {!noBackButtonPathsForMobileDevice.includes(pathname) && (
                <BackButton onClick={() => window.history.back()} />
              )}
              <div className="h-[54px] flex-1 bg-gray-200 animate-pulse rounded-full" />
            </div>
          ) : (
            <div className="px-6 py-4">
              <div className="h-[50px] w-full bg-gray-200 animate-pulse rounded-full" />
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <DesktopHeader {...props} />
      <MobileHeader {...props} />
    </>
  );
};

export default Header;
